<template>
    <vx-table-row>
        <vx-table-row-title class="break-words">
            {{ model.id === userId ? 'You' : model.name }}
            <div class="text-sm text-slate-500 font-normal mt-1">{{ model.email }}</div>
        </vx-table-row-title>

        <vx-table-row-item>
            <template v-slot:label> Team Role: </template>
            {{ TEAM_USER_ROLES[model.role] || '-' }}
        </vx-table-row-item>

        <vx-table-row-item v-if="FEATURES.contact_groups">
            <template v-slot:label> OU Access: </template>
            {{ orgUnitCount }}
        </vx-table-row-item>

        <vx-table-row-item v-if="FEATURES.contact_groups">
            <template v-slot:label> Group Access: </template>
            {{ groupCount }}
        </vx-table-row-item>

        <vx-table-row-buttons v-if="model.role !== 'owner' && model.id !== userId && isAdminOrOwner && !props.readOnly">
            <vx-dropdown placement="right">
                <vx-button data-test="dropdown"
                    type="button"
                    color="muted"
                    outline
                    class="w-[45px]">
                    <font-awesome-icon widthAuto :icon="faEllipsis" />
                </vx-button>

                <template v-slot:items>
                    <vx-dropdown-item v-if="FEATURES.contact_groups"
                        data-test="add-to-org-unit"
                        @click="emit('addToOrgUnit', model)">
                        Add to OU
                        <font-awesome-icon widthAuto :icon="faBuildingFlag"></font-awesome-icon>
                    </vx-dropdown-item>
                    <vx-dropdown-item v-if="FEATURES.contact_groups"
                        data-test="add-to-group"
                        @click="emit('addToGroup', model)">
                        Add to Group
                        <font-awesome-icon widthAuto :icon="faGrid2Plus"></font-awesome-icon>
                    </vx-dropdown-item>
                    <vx-dropdown-item data-test="delete"
                        @click="confirmModal = true">
                        Remove from Team
                        <font-awesome-icon widthAuto :icon="faCircleMinus"></font-awesome-icon>
                    </vx-dropdown-item>
                </template>
            </vx-dropdown>

            <vx-button
                v-if="!props.readOnly"
                @click="editModal = true"
                data-test="edit"
                color="muted"
                outline>
                Edit <font-awesome-icon widthAuto :icon="faFilePen"/>
            </vx-button>
        </vx-table-row-buttons>

        <warning-modal v-model:visible="confirmModal"
            confirmText="Yes, remove user"
            message="The user will immediately lose access to this team."
            type="danger"
            @confirm="deleteUser">
        </warning-modal>

        <team-user-modal-form :modelValue="model" :teamId="props.teamId" v-model:visible="editModal" @updated="emit('updated')"></team-user-modal-form>
    </vx-table-row>
</template>

<script setup>
import {
    faBuildingFlag,
    faCircleMinus,
    faEllipsis,
    faFilePen,
    faGrid2Plus,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { useToasted } from '~/composables/useToasted';
import { TEAM_USER_ROLES } from "~/constants/roles";
import { usersClient } from "~/services";
import TeamUserModalForm from './TeamUserModalForm.vue';
import { computed, inject, ref } from "vue";
import WarningModal from '~/components/modals/WarningModal.vue';
import { catchRequestError } from "~/utils/request";
import { formatNumber } from "~/utils/helpers";

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    readOnly: {
        type: Boolean,
        default: false,
    },
});

const toasted = useToasted();
const userId = Spark.state.user.id;

const isAdminOrOwner = Spark.isAdminOrOwner;
const FEATURES = inject('FEATURES');

const model = defineModel({
    type: Object,
    required: true,
});

const editModal = ref(false);
const confirmModal = ref(false);

const emit = defineEmits(['updated', 'deleted', 'addToGroup', 'addToOrgUnit']);

const orgUnitCount = computed(() => ['admin', 'owner'].includes(model.value.role)
    ? 'All'
    : formatNumber(model.value.org_unit_count)
);

const groupCount = computed(() => ['admin', 'owner'].includes(model.value.role)
    ? 'All'
    : formatNumber(model.value.group_count)
);

const deleteUser = async () => {
    try {
        await usersClient.deleteUser(props.teamId, model.value.id);
        emit('deleted', model.value.id);
        toasted.success(`${model.value.name || model.value.email} removed from Team`)
    } catch (e) {
        toasted.error(catchRequestError(e))
    }
};

</script>
